import { useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { apiClient } from "../../constants.ts";
import { Button, inputClass, labelClass, PageSpinner } from "./components.tsx";
import { useAuth, useQuery } from "./db.ts";

const hostsDisplayStyle =
  "text-xs text-text-tertiary-light dark:text-text-tertiary truncate";

const HostsEditor = (
  { hosts, secretId, botId, token }: {
    hosts: string[];
    secretId: string;
    botId: string;
    token: string;
  },
) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const startEditing = () => {
    setDraft(hosts.join(", "));
    setEditing(true);
  };

  const save = () => {
    const parsed = draft.split(",").map((h) => h.trim()).filter((h) =>
      h.length > 0
    );
    toast.promise(
      apiClient({
        token,
        endpoint: "update-secret",
        payload: { botId, secretId, hosts: parsed },
      }).then((res) => {
        if (!res.success) throw new Error(res.error);
        setEditing(false);
      }),
      {
        loading: "Saving...",
        success: "Hosts updated",
        error: (e) => `Error: ${e.message}`,
      },
    );
  };

  return editing
    ? (
      <div className="flex items-center gap-2 mt-1">
        <input
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          className={inputClass}
          placeholder="Comma-separated hostnames, or leave empty to disable"
          onKeyDown={(e) => e.key === "Enter" && save()}
        />
        <Button onClick={save} className="text-xs shrink-0">Save</Button>
        <Button
          onClick={() => setEditing(false)}
          className="text-xs shrink-0"
        >
          Cancel
        </Button>
      </div>
    )
    : (
      <button
        type="button"
        onClick={startEditing}
        className={`${hostsDisplayStyle} hover:text-text-secondary-light dark:hover:text-text-secondary cursor-pointer hover:underline inline-flex items-center gap-1`}
        title="Click to edit allowed hosts"
      >
        {hosts.length > 0
          ? (
            <>
              {hosts.join(", ")}
              <span className="opacity-50 text-[10px]">&#9998;</span>
            </>
          )
          : <span className="italic">Add hosts...</span>}
      </button>
    );
};

const editableFieldStyle =
  "hover:text-text-secondary-light dark:hover:text-text-secondary cursor-pointer hover:underline inline-flex items-center gap-1";

const InlineEditor = (
  { value, onSave, className, placeholder, title }: {
    value: string;
    onSave: (v: string) => Promise<void>;
    className?: string;
    placeholder?: string;
    title?: string;
  },
) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const startEditing = () => {
    setDraft(value);
    setEditing(true);
  };

  const save = () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) return;
    toast.promise(
      onSave(trimmed).then(() => setEditing(false)),
      {
        loading: "Saving...",
        success: "Updated",
        error: (e) => `Error: ${e.message}`,
      },
    );
  };

  return editing
    ? (
      <div className="flex items-center gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          className={inputClass}
          placeholder={placeholder}
          onKeyDown={(e) => e.key === "Enter" && save()}
        />
        <Button onClick={save} className="text-xs shrink-0">Save</Button>
        <Button
          onClick={() => setEditing(false)}
          className="text-xs shrink-0"
        >
          Cancel
        </Button>
      </div>
    )
    : (
      <button
        type="button"
        onClick={startEditing}
        className={`${editableFieldStyle} ${className ?? ""}`}
        title={title ?? "Click to edit"}
      >
        {value}
        <span className="opacity-50 text-[10px]">&#9998;</span>
      </button>
    );
};

const fieldLabelStyle =
  "text-xs font-medium text-text-tertiary-light dark:text-text-tertiary uppercase tracking-wide mr-2 shrink-0";

const maskedValueStyle =
  "text-xs text-text-tertiary-light dark:text-text-tertiary tracking-widest";

const SecretValueEditor = (
  { name, hosts, botId, token }: {
    name: string;
    hosts: string[];
    botId: string;
    token: string;
  },
) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const save = () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) return;
    toast.promise(
      apiClient({
        token,
        endpoint: "set-secret",
        payload: {
          botId,
          name,
          value: trimmed,
          hosts,
        },
      }).then((res) => {
        if (!res.success) throw new Error(res.error);
        setEditing(false);
        setDraft("");
      }),
      {
        loading: "Saving...",
        success: "Value updated",
        error: (e) => `Error: ${e.message}`,
      },
    );
  };

  return editing
    ? (
      <div className="flex items-center gap-2 mt-1">
        <input
          type="password"
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          className={inputClass}
          placeholder="Enter new value"
          onKeyDown={(e) => e.key === "Enter" && save()}
        />
        <Button onClick={save} className="text-xs shrink-0">Save</Button>
        <Button
          onClick={() => {
            setEditing(false);
            setDraft("");
          }}
          className="text-xs shrink-0"
        >
          Cancel
        </Button>
      </div>
    )
    : (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={`${maskedValueStyle} ${editableFieldStyle}`}
        title="Click to replace value"
      >
        {"••••••••"}
        <span className="opacity-50 text-[10px]">&#9998;</span>
      </button>
    );
};

const SecretRow = (
  { name, hosts, secretId, botId, token }: {
    name: string;
    hosts: string[];
    secretId: string;
    botId: string;
    token: string;
  },
) => (
  <div className="flex flex-col gap-3 py-3 border-b border-border-subtle-light dark:border-border-subtle">
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center min-w-0">
        <span className={fieldLabelStyle}>Name</span>
        <InlineEditor
          value={name}
          className="font-medium text-sm"
          placeholder="Secret name"
          title="Click to rename"
          onSave={(n) =>
            apiClient({
              token,
              endpoint: "update-secret",
              payload: { botId, secretId, name: n },
            }).then((res) => {
              if (!res.success) throw new Error(res.error);
            })}
        />
      </div>
      <Button
        onClick={() =>
          toast.promise(
            apiClient({
              token,
              endpoint: "delete-secret",
              payload: { botId, secretId },
            }).then((res) => {
              if (!res.success) throw new Error(res.error);
            }),
            {
              loading: "Deleting...",
              success: `Secret "${name}" deleted`,
              error: (e) => `Error: ${e.message}`,
            },
          )}
        className="text-sm shrink-0 !bg-danger hover:!bg-danger-hover !text-white"
      >
        Delete
      </Button>
    </div>
    <div className="flex items-center">
      <span className={fieldLabelStyle}>Value</span>
      <SecretValueEditor
        name={name}
        hosts={hosts}
        botId={botId}
        token={token}
      />
    </div>
    <div className="flex items-center">
      <span className={fieldLabelStyle}>Hosts</span>
      <HostsEditor
        hosts={hosts}
        secretId={secretId}
        botId={botId}
        token={token}
      />
    </div>
  </div>
);

const VariableRow = (
  { name, value, variableId, botId, token }: {
    name: string;
    value: string;
    variableId: string;
    botId: string;
    token: string;
  },
) => {
  const updateVariable = (field: string) => (v: string) =>
    apiClient({
      token,
      endpoint: "update-variable",
      payload: { botId, variableId, [field]: v },
    }).then((res) => {
      if (!res.success) throw new Error(res.error);
    });

  return (
    <div className="flex flex-col gap-3 py-3 border-b border-border-subtle-light dark:border-border-subtle">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center min-w-0">
          <span className={fieldLabelStyle}>Name</span>
          <InlineEditor
            value={name}
            className="font-medium text-sm"
            placeholder="Variable name"
            title="Click to rename"
            onSave={updateVariable("name")}
          />
        </div>
        <Button
          onClick={() =>
            toast.promise(
              apiClient({
                token,
                endpoint: "delete-variable",
                payload: { botId, variableId },
              }).then((res) => {
                if (!res.success) throw new Error(res.error);
              }),
              {
                loading: "Deleting...",
                success: `Variable "${name}" deleted`,
                error: (e) => `Error: ${e.message}`,
              },
            )}
          className="text-sm shrink-0 !bg-danger hover:!bg-danger-hover !text-white"
        >
          Delete
        </Button>
      </div>
      <div className="flex items-center">
        <span className={fieldLabelStyle}>Value</span>
        <InlineEditor
          value={value || "(empty)"}
          className="text-xs text-text-tertiary-light dark:text-text-tertiary font-mono"
          placeholder="Value"
          title="Click to edit value"
          onSave={updateVariable("value")}
        />
      </div>
    </div>
  );
};

const AddSecretForm = (
  { botId, token, onDone }: {
    botId: string;
    token: string;
    onDone: () => void;
  },
) => {
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [hosts, setHosts] = useState("");

  const canSubmit = name.trim().length > 0 && value.trim().length > 0 &&
    hosts.trim().length > 0;

  const handleSubmit = () =>
    toast.promise(
      apiClient({
        token,
        endpoint: "set-secret",
        payload: {
          botId,
          name: name.trim(),
          value: value.trim(),
          hosts: hosts.split(",").map((h) => h.trim()).filter((h) =>
            h.length > 0
          ),
        },
      }).then((res) => {
        if (!res.success) throw new Error(res.error);
        setName("");
        setValue("");
        setHosts("");
        onDone();
      }),
      {
        loading: "Saving...",
        success: "Secret saved",
        error: (e) => `Error: ${e.message}`,
      },
    );

  return (
    <div className="flex flex-col gap-3 p-4 border border-border-subtle-light dark:border-border-subtle rounded-xl">
      <div>
        <label className={labelClass}>Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          className={inputClass}
          placeholder="e.g. GITHUB_TOKEN"
        />
      </div>
      <div>
        <label className={labelClass}>Value</label>
        <input
          type="password"
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
          className={inputClass}
          placeholder="The API key or token"
        />
      </div>
      <div>
        <label className={labelClass}>Allowed hosts (comma-separated)</label>
        <input
          value={hosts}
          onChange={(e) => setHosts(e.currentTarget.value)}
          className={inputClass}
          placeholder="e.g. api.github.com, github.com"
        />
      </div>
      <div className="flex gap-2">
        <Button onClick={handleSubmit} disabled={!canSubmit}>Add Secret</Button>
        <Button onClick={onDone}>Cancel</Button>
      </div>
    </div>
  );
};

const AddVariableForm = (
  { botId, token, onDone }: {
    botId: string;
    token: string;
    onDone: () => void;
  },
) => {
  const [name, setName] = useState("");
  const [value, setValue] = useState("");

  const canSubmit = name.trim().length > 0 && value.trim().length > 0;

  const handleSubmit = () =>
    toast.promise(
      apiClient({
        token,
        endpoint: "set-variable",
        payload: { botId, name: name.trim(), value: value.trim() },
      }).then((res) => {
        if (!res.success) throw new Error(res.error);
        setName("");
        setValue("");
        onDone();
      }),
      {
        loading: "Saving...",
        success: "Variable saved",
        error: (e) => `Error: ${e.message}`,
      },
    );

  return (
    <div className="flex flex-col gap-3 p-4 border border-border-subtle-light dark:border-border-subtle rounded-xl">
      <div>
        <label className={labelClass}>Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          className={inputClass}
          placeholder="e.g. default-language"
        />
      </div>
      <div>
        <label className={labelClass}>Value</label>
        <input
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
          className={inputClass}
          placeholder="e.g. en"
        />
      </div>
      <div className="flex gap-2">
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          Add Variable
        </Button>
        <Button onClick={onDone}>Cancel</Button>
      </div>
    </div>
  );
};

const sectionCardStyle =
  "bg-surface-raised-light dark:bg-surface-raised rounded-lg shadow-md p-6";

const SecretsSection = (
  { secrets, botId, token }: {
    secrets: { id: string; name: string; hosts: string[] }[];
    botId: string;
    token: string;
  },
) => {
  const [adding, setAdding] = useState(false);

  return (
    <div className={sectionCardStyle}>
      <h2 className="text-xl font-semibold mb-2">Secrets</h2>
      <p className="text-sm text-text-secondary-light dark:text-text-tertiary mb-4">
        Encrypted with AES-GCM and never stored in plaintext. The LLM never sees
        the actual values. On the VM, secrets are replaced with machine-specific
        HMAC placeholders that a reverse proxy swaps for real credentials only
        on outbound requests to the approved hosts. You can also safely paste
        secrets directly in the chat conversation, where they are automatically
        detected, redacted, and encrypted.
      </p>
      {secrets.length > 0
        ? (
          <div className="mb-4">
            {secrets.map((s) => (
              <SecretRow
                key={s.id}
                name={s.name}
                hosts={s.hosts}
                secretId={s.id}
                botId={botId}
                token={token}
              />
            ))}
          </div>
        )
        : (
          <div className="text-sm text-text-tertiary-light dark:text-text-tertiary mb-4">
            No secrets stored yet.
          </div>
        )}
      {adding
        ? (
          <AddSecretForm
            botId={botId}
            token={token}
            onDone={() => setAdding(false)}
          />
        )
        : <Button onClick={() => setAdding(true)}>Add Secret</Button>}
    </div>
  );
};

const VariablesSection = (
  { variables, botId, token }: {
    variables: { id: string; name: string; value: string }[];
    botId: string;
    token: string;
  },
) => {
  const [adding, setAdding] = useState(false);

  return (
    <div className={sectionCardStyle}>
      <h2 className="text-xl font-semibold mb-2">Variables</h2>
      <p className="text-sm text-text-secondary-light dark:text-text-tertiary mb-4">
        Plaintext key-value pairs visible to the LLM. Use these for
        non-sensitive configuration the agent needs to reference.
      </p>
      {variables.length > 0
        ? (
          <div className="mb-4">
            {variables.map((v) => (
              <VariableRow
                key={v.id}
                name={v.name}
                value={v.value}
                variableId={v.id}
                botId={botId}
                token={token}
              />
            ))}
          </div>
        )
        : (
          <div className="text-sm text-text-tertiary-light dark:text-text-tertiary mb-4">
            No variables stored yet.
          </div>
        )}
      {adding
        ? (
          <AddVariableForm
            botId={botId}
            token={token}
            onDone={() => setAdding(false)}
          />
        )
        : <Button onClick={() => setAdding(true)}>Add Variable</Button>}
    </div>
  );
};

export const SecretsConfig = (
  { id, botId }: { id: string; botId: string },
) => {
  const token = useAuth().user?.refresh_token;
  const { data, error, isLoading } = useQuery({
    bots: { $: { where: { id } }, secrets: {}, variables: {} },
  });
  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error: {error.message}</div>;
  const [bot] = data.bots;
  if (!bot) return <div>No bot found</div>;
  if (!token) return <div>Not authenticated</div>;

  const secrets: {
    id: string;
    name: string;
    hosts: string[];
    conversationId?: string;
  }[] = bot.secrets ?? [];
  const botLevelSecrets = secrets.filter((s) => !s.conversationId);
  const variables: { id: string; name: string; value: string }[] =
    bot.variables ?? [];

  return (
    <div className="flex flex-col gap-6 min-w-0 w-full">
      <SecretsSection secrets={botLevelSecrets} botId={botId} token={token} />
      <VariablesSection variables={variables} botId={botId} token={token} />
    </div>
  );
};
